import { memo, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import { FrameCanvas } from './FrameCanvas';
import { buildPanorama } from './reslice';
import { frameToPngDataUrl } from './exportFrame';
import type { Point } from './hermite';
import type { ManifestV2, Volume } from './types';
import { containBoxStyle } from './viewerTheme';

const WHITE = '#ffffff';
const BORDER = '#e0e6eb';
const INK = '#1c2734';
const MUTED = '#5b6774';
const PANE_BG = '#000000';
const PANE_BORDER = '#cfd6dd';
const ACCENT = '#45c3d6';
const MONO = "ui-monospace,'SF Mono','Cascadia Code',monospace";

const TROUGH_MIN = 1;
const TROUGH_MAX = 30;
const TROUGH_DEFAULT = 10;

export const PanoramaPanel = memo(function PanoramaPanel({
    volume,
    manifest,
    pts,
    normals,
    center,
    width,
    flipVertical = false,
    troughMm,
    onTroughMm,
    live,
    exportRef
}: {
    volume: Volume;
    manifest: ManifestV2;
    pts: Point[];
    normals: Point[];
    center: number;
    width: number;
    /** Reverses the superior/inferior (z) orientation — see `displayZ` in volume.ts. */
    flipVertical?: boolean;
    troughMm: number;
    /** When provided, an in-pane control bar (like the cephalogram) lets the user
     *  tune the slab thickness. Omit for a static, read-only panorama. */
    onTroughMm?: (v: number) => void;
    live?: boolean;
    /** Receives a function returning a PNG data URL of the panorama (for export). */
    exportRef?: React.MutableRefObject<(() => string | null) | null>;
}) {
    const sx = manifest.spacing_mm.x;
    const sz = manifest.spacing_mm.z;
    const [controlsOpen, setControlsOpen] = useState(false);

    // Drag the slab locally for a responsive thumb/preview; push the upstream
    // update (which re-renders the whole viewer) only once the drag settles.
    const [slab, setSlab] = useState(troughMm);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (!timer.current) setSlab(troughMm);
    }, [troughMm]);
    useEffect(
        () => () => {
            if (timer.current) clearTimeout(timer.current);
        },
        []
    );
    const pushSlab = (v: number) => {
        setSlab(v);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            timer.current = null;
            onTroughMm?.(v);
        }, 120);
    };

    // Defer the (synchronous, full-width) reslice so dragging the slab slider
    // stays responsive — the pane shows the previous frame until the rebuild lands.
    const deferredSlab = useDeferredValue(slab);
    const frame = useMemo(
        () => buildPanorama(volume, pts, normals, deferredSlab / sx, undefined, flipVertical),
        [volume, pts, normals, deferredSlab, sx, flipVertical]
    );
    const aspect = (frame.w * sx) / (frame.h * sz);
    const hasControls = !!onTroughMm;

    // Export handle: render the current panorama off-screen at the display aspect.
    useEffect(() => {
        if (!exportRef) return;
        exportRef.current = () => frameToPngDataUrl(frame, center, width, aspect);
        return () => {
            exportRef.current = null;
        };
    }, [exportRef, frame, center, width, aspect]);

    return (
        <div
            className="relative flex h-full w-full items-center justify-center overflow-hidden"
            style={{
                background: PANE_BG,
                border: `1px solid ${PANE_BORDER}`,
                borderRadius: 7,
                fontFamily: MONO,
                containerType: 'size'
            }}
        >
            <div className="relative max-h-full max-w-full" style={containBoxStyle(aspect)}>
                <FrameCanvas
                    frame={frame}
                    center={center}
                    width={width}
                    className="block h-full w-full"
                />
            </div>

            <div
                className="pointer-events-none absolute"
                style={{
                    top: 9,
                    left: 11,
                    fontWeight: 600,
                    fontSize: 10.5,
                    letterSpacing: '0.13em',
                    color: ACCENT
                }}
            >
                PANORAMA{' '}
                <span style={{ fontWeight: 400, letterSpacing: 'normal', color: '#6a7883' }}>
                    · {live ? 'live preview' : 'curved MPR'}
                </span>
            </div>

            {hasControls && (
                <>
                    {/* Controls toggle (top-right, over the render) */}
                    <button
                        onClick={() => setControlsOpen((v) => !v)}
                        title={controlsOpen ? 'Hide panorama controls' : 'Show panorama controls'}
                        aria-label={
                            controlsOpen ? 'Hide panorama controls' : 'Show panorama controls'
                        }
                        className="absolute grid place-items-center"
                        style={{
                            top: 8,
                            right: 9,
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            background: controlsOpen
                                ? 'rgba(14,151,176,.28)'
                                : 'rgba(255,255,255,.08)',
                            border: `1px solid ${controlsOpen ? 'rgba(143,208,220,.55)' : 'rgba(255,255,255,.16)'}`,
                            color: controlsOpen ? '#8fd0dc' : '#c6d0d8',
                            cursor: 'pointer'
                        }}
                    >
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                        >
                            <line x1="4" y1="8" x2="20" y2="8" />
                            <circle cx="9" cy="8" r="2.2" fill="currentColor" stroke="none" />
                            <line x1="4" y1="16" x2="20" y2="16" />
                            <circle cx="15" cy="16" r="2.2" fill="currentColor" stroke="none" />
                        </svg>
                    </button>

                    {/* Slab controls — floating bar over the bottom of the render */}
                    {controlsOpen && (
                        <div
                            className="absolute flex flex-wrap items-center gap-x-4 gap-y-2"
                            style={{
                                left: 11,
                                right: 11,
                                bottom: 11,
                                padding: '9px 13px',
                                background: 'rgba(247,249,251,.95)',
                                border: `1px solid ${BORDER}`,
                                borderRadius: 9,
                                boxShadow: '0 8px 24px rgba(24,40,56,.28)'
                            }}
                        >
                            <span style={{ fontWeight: 600, fontSize: 12, color: INK }}>
                                Panorama
                            </span>
                            <label
                                className="flex flex-1 items-center gap-2"
                                style={{ fontSize: 11.5, color: MUTED, minWidth: 160 }}
                            >
                                <span style={{ letterSpacing: '0.02em' }}>Slab</span>
                                <input
                                    type="range"
                                    min={TROUGH_MIN}
                                    max={TROUGH_MAX}
                                    step={1}
                                    value={slab}
                                    onChange={(e) => pushSlab(Number(e.target.value))}
                                    className="flex-1 cursor-pointer"
                                    style={{ accentColor: '#0e97b0', height: 4 }}
                                />
                                <span
                                    style={{
                                        fontFamily: MONO,
                                        fontSize: 11,
                                        width: 42,
                                        textAlign: 'right',
                                        color: INK
                                    }}
                                >
                                    {slab.toFixed(0)} mm
                                </span>
                            </label>
                            <button
                                onClick={() => pushSlab(TROUGH_DEFAULT)}
                                style={{
                                    padding: '5px 11px',
                                    borderRadius: 7,
                                    background: WHITE,
                                    border: `1px solid ${BORDER}`,
                                    color: MUTED,
                                    fontSize: 11,
                                    cursor: 'pointer'
                                }}
                            >
                                Reset
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});
